import { useEffect, useMemo, useState } from "react";

import { useCheckout } from "../../context/CheckoutContext";
import { useTranslation } from "../../hooks/useTranslation";
import { formatCurrency, minorToMajor } from "../../i18n/utils/helpers";
import { useSubscriptionEstimate } from "../../hooks/useSubscriptionEstimate";
import { updateSession } from "../../utils/paymentSession";
import {
  resolveTrialMetadata,
  isZeroAmountTrial,
  formatTrialEndDate,
} from "../../utils/trialCheckout";
import {
  getAddonUnitPriceMinor,
  indexEstimateLinesByEntityId,
  resolveEstimateLineDisplay,
  resolveCatalogItemDisplayName,
  looksTechnicalId,
  resolveCatalogOrHydratedPrice,
  supportsLineItemQuantity,
  computeEstimateSummaryTotals,
  computeSelectedItemsBaseline,
  computeRecurringChargeTotal,
  computeCheckoutDiscount,
  shouldShowSubtotalRow,
  resolvePreDiscountSubtotal,
} from "../../utils/lineItemDisplay";

import {
  NeoSubscribeLabel,
  NeoBigPrice,
  NeoTrialCopy,
  NeoPlanGroup,
  NeoLineItemRow,
  NeoLineThumb,
  NeoLineBody,
  NeoLineTitle,
  NeoLineSub,
  NeoLinePrice,
  NeoAddonList,
  NeoAddonRow,
  NeoAddonBody,
  NeoAddonLabel,
  NeoSummaryRow,
  NeoCouponPill,
  NeoCouponNote,
  NeoDivider,
  NeoTaxLabel,
  NeoCouponInputRow,
  NeoCouponInput,
  NeoCouponRedeemBtn,
  NeoCouponError,
  NeoCouponChipRow,
  NeoCouponChip,
  NeoCouponRemoveBtn,
  NeoCouponClearAll,
  SkeletonBlock,
  SkeletonCircle,
  SkeletonRow,
  SkeletonLineItemRow,
  SkeletonLineBody,
} from "./NeoStyles";

/* ─────────────────────────────────────────────────────────────────────────
   Skeleton loader — mirrors the exact shape of the summary panel.
   Shown while the first estimate API call is in flight.
   ───────────────────────────────────────────────────────────────────────── */
const NeoOrderSummarySkeleton = () => (
  <div aria-label="Loading order summary" aria-busy="true">
    {/* "Subscribe to …" label */}
    <SkeletonBlock $h="13px" $w="55%" $mb="10px" />
    {/* Big price */}
    <SkeletonBlock $h="36px" $w="42%" $r="6px" $mb="10px" />
    {/* Trial copy — optional, shorter */}
    <SkeletonBlock $h="12px" $w="80%" $mb="24px" />

    {/* Line item row */}
    <SkeletonLineItemRow>
      <SkeletonCircle $size="32px" style={{ borderRadius: "4px" }} />
      <SkeletonLineBody>
        <SkeletonBlock $h="13px" $w="60%" />
        <SkeletonBlock $h="11px" $w="45%" />
        <SkeletonBlock $h="11px" $w="35%" />
      </SkeletonLineBody>
      <SkeletonBlock $h="13px" $w="14%" />
    </SkeletonLineItemRow>

    {/* Subtotal row */}
    <SkeletonRow $mb="10px">
      <SkeletonBlock $h="13px" $w="26%" />
      <SkeletonBlock $h="13px" $w="18%" />
    </SkeletonRow>

    {/* Tax row */}
    <SkeletonRow $mb="14px">
      <SkeletonBlock $h="13px" $w="20%" />
      <SkeletonBlock $h="13px" $w="14%" />
    </SkeletonRow>

    {/* Divider */}
    <SkeletonBlock $h="1px" $w="100%" $mb="12px" style={{ opacity: 0.5 }} />

    {/* Total row */}
    <SkeletonRow>
      <SkeletonBlock $h="15px" $w="38%" />
      <SkeletonBlock $h="15px" $w="22%" />
    </SkeletonRow>
  </div>
);

/**
 * Tag/coupon icon — matches the small label-style icon Stripe shows next to coupon codes.
 */
const TagIcon = () => (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M2.5 7.5L7.5 2.5H13.5V8.5L8.5 13.5L2.5 7.5Z"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinejoin="round"
    />
    <circle cx="10.5" cy="5.5" r="0.9" fill="currentColor" />
  </svg>
);

const InfoIcon = () => (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle cx="8" cy="8" r="6.4" stroke="currentColor" strokeWidth="1.2" />
    <path
      d="M8 7.2v3.6M8 5.6v.4"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
  </svg>
);

const unitKeyMap = {
  month: { single: "unitMonth", plural: "unitMonths" },
  year: { single: "unitYear", plural: "unitYears" },
  week: { single: "unitWeek", plural: "unitWeeks" },
  day: { single: "unitDay", plural: "unitDays" },
};

const periodAdverbKey = (period, periodUnit) => {
  if (!periodUnit) return null;
  const unit = periodUnit.toLowerCase();
  const n = Number(period) || 1;
  if (n !== 1) return { type: "every", n, unit };
  switch (unit) {
    case "month":
      return { type: "billed", key: "billedMonthly" };
    case "year":
      return { type: "billed", key: "billedYearly" };
    case "week":
      return { type: "billed", key: "billedWeekly" };
    case "day":
      return { type: "billed", key: "billedDaily" };
    default:
      return null;
  }
};

const NeoOrderSummary = ({
  itemDetails,
  platformItemDetails,
  selectedAddons,
  billingConnectionId,
  planId,
  formData,
  presetCouponCodes = [],
  checkoutPageConfDto,
  checkoutPageFields,
  sessionId,
}) => {
  const { t, language } = useTranslation();
  const { setEstimatedOrder, clientCountry, sessionTrial, forceTrialPeriodDays } =
    useCheckout();

  const platformItem = platformItemDetails?.[0];
  const currencyCode =
    itemDetails?.currencyCode || platformItem?.currencyCode;
  const period = platformItem?.period || itemDetails?.period;
  const periodUnit = platformItem?.periodUnit || itemDetails?.periodUnit;

  const merchantName =
    checkoutPageConfDto?.companyName || checkoutPageConfDto?.brand || "";

  const isCouponEnabled = checkoutPageFields?.includes("COUPON_CODE");

  /* ─────────── Coupon state ─────────── */
  const normalizedPreset = useMemo(() => {
    if (!Array.isArray(presetCouponCodes)) return [];
    return Array.from(
      new Set(
        presetCouponCodes.map((c) => c?.trim()).filter((c) => !!c)
      )
    );
  }, [presetCouponCodes]);

  const [appliedCoupons, setAppliedCoupons] = useState(normalizedPreset);
  const [couponInput, setCouponInput] = useState("");
  const [couponError, setCouponError] = useState("");
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  // Once the user explicitly adds or removes a coupon, use appliedCoupons
  // verbatim (even when empty) instead of falling back to normalizedPreset.
  const [hasUserModifiedCoupons, setHasUserModifiedCoupons] = useState(false);

  // If preset coupons change (e.g. URL update), reflect them
  useEffect(() => {
    const same =
      normalizedPreset.length === appliedCoupons.length &&
      normalizedPreset.every((c, i) => c === appliedCoupons[i]);
    if (!same && normalizedPreset.length) {
      setAppliedCoupons(normalizedPreset);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedPreset]);

  const effectiveCouponCodes = useMemo(() => {
    // After the user has touched the coupon list, use appliedCoupons directly
    // (even if empty). Before any user action, fall back to URL preset coupons.
    if (hasUserModifiedCoupons) return appliedCoupons;
    return appliedCoupons.length > 0 ? appliedCoupons : normalizedPreset;
  }, [appliedCoupons, normalizedPreset, hasUserModifiedCoupons]);

  const { estimatedOrder, isEstimating } = useSubscriptionEstimate({
    billingConnectionId,
    planId,
    platformItemDetails,
    platformItem,
    selectedAddons,
    formData,
    clientCountry,
    effectiveCouponCodes,
    setEstimatedOrder,
    checkoutCurrency: currencyCode,
    forceTrialPeriodDays,
    billingPlatformName:
      itemDetails?.billingPlatformName ||
      checkoutPageConfDto?.billingPlatformName ||
      platformItem?.billingPlatformName,
  });

  /* ─────────── Coupon handlers ─────────── */
  const syncCouponState = async (nextCoupons) => {
    setCouponError("");
    setIsApplyingCoupon(true);
    // Mark that the user has explicitly modified the coupon list so we stop
    // falling back to URL-preset coupons (even if the new list is empty).
    setHasUserModifiedCoupons(true);
    try {
      if (sessionId) {
        await updateSession(sessionId, {
          billingData: { couponCodes: nextCoupons },
        });
      }
      setAppliedCoupons(nextCoupons);
    } catch (err) {
      console.error("Coupon sync failed:", err);
      setCouponError(t("checkout.orderSummary.couponApplyFailed"));
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const handleRedeemCoupon = async () => {
    const normalized = couponInput?.trim();
    if (!normalized) {
      setCouponError(t("checkout.orderSummary.couponEmpty"));
      return;
    }
    if (
      appliedCoupons.some(
        (existing) => existing.toLowerCase() === normalized.toLowerCase()
      )
    ) {
      setCouponError(t("checkout.orderSummary.couponDuplicate"));
      return;
    }
    const next = [...appliedCoupons, normalized];
    await syncCouponState(next);
    setCouponInput("");
  };

  const handleRemoveCoupon = async (code) => {
    const next = appliedCoupons.filter((c) => c !== code);
    await syncCouponState(next);
  };

  const handleClearCoupons = async () => {
    if (!appliedCoupons.length) return;
    await syncCouponState([]);
  };

  /* ─────────── Derived amounts ─────────── */
  const estimateLinesById = useMemo(
    () => indexEstimateLinesByEntityId(estimatedOrder?.lineItems),
    [estimatedOrder?.lineItems]
  );

  const priceType = estimatedOrder?.priceType;
  const displayOptions = { priceType };

  const baselineSubtotal = computeSelectedItemsBaseline({
    planId,
    platformItem,
    selectedAddons,
    estimateLinesById,
    priceType,
    currencyCode,
  });

  const planEstimateLine = estimateLinesById.get(planId);
  const planDisplay = planEstimateLine
    ? resolveEstimateLineDisplay(planEstimateLine, displayOptions)
    : null;
  const planUnitAmount = planDisplay
    ? planDisplay.displayTotal
    : minorToMajor(platformItem?.price || 0, currencyCode);

  const summaryTotals = computeEstimateSummaryTotals(estimatedOrder);
  const subtotal = summaryTotals?.subtotal ?? baselineSubtotal;
  const totalTaxAmount = summaryTotals?.totalTaxAmount ?? 0;
  const totalDue = summaryTotals?.totalDue ?? baselineSubtotal;
  const isTaxExclusive = summaryTotals?.isTaxExclusive ?? false;

  const catalogBaseline =
    minorToMajor(platformItem?.price || 0, currencyCode) +
    (selectedAddons || []).reduce(
      (acc, a) =>
        acc +
        minorToMajor(getAddonUnitPriceMinor(a), currencyCode) * (a.quantity || 1),
      0
    );

  const preDiscountSubtotal = resolvePreDiscountSubtotal({
    estimatedOrder,
    baselineSubtotal,
    catalogBaseline,
  });

  const discount = computeCheckoutDiscount({
    estimatedOrder,
    baselineSubtotal,
    summaryTotals,
    couponCodes: effectiveCouponCodes,
    catalogBaseline,
  });

  const hasAppliedCoupon = effectiveCouponCodes.length > 0;

  const trialMeta =
    resolveTrialMetadata(estimatedOrder) ||
    resolveTrialMetadata({ trial: sessionTrial });

  const recurringTotal = (() => {
    const fromTrial = Number(trialMeta?.recurringAmountAfterTrial);
    if (trialMeta?.inTrial && !Number.isNaN(fromTrial) && fromTrial > 0) {
      return fromTrial;
    }
    return computeRecurringChargeTotal(
      discount > 0 ? preDiscountSubtotal : baselineSubtotal,
      {
        isTaxExclusive,
        totalTaxAmount,
      }
    );
  })();
  const showSubtotal = shouldShowSubtotalRow(subtotal, totalDue, discount);
  const subtotalDisplay =
    discount > 0 ? preDiscountSubtotal : subtotal;

  const fmt = (n) => formatCurrency(n, currencyCode, language);

  /* ─────────── Period text helpers ─────────── */
  const adverb = periodAdverbKey(period, periodUnit);
  const billedText = (() => {
    if (!adverb) return "";
    if (adverb.type === "billed") return t(`checkout.neo.${adverb.key}`);
    const unitMap = unitKeyMap[adverb.unit] || unitKeyMap.month;
    const unitWord = t(`checkout.neo.${adverb.n === 1 ? unitMap.single : unitMap.plural}`);
    return t("checkout.neo.billedEvery", { variables: { n: adverb.n, unit: unitWord } });
  })();

  const periodNounText = (() => {
    if (!periodUnit) return "";
    const unit = periodUnit.toLowerCase();
    const n = Number(period) || 1;
    const unitMap = unitKeyMap[unit] || unitKeyMap.month;
    const word = t(`checkout.neo.${n === 1 ? unitMap.single : unitMap.plural}`);
    if (n === 1) return word;
    return `${n} ${word}`;
  })();

  /* ─────────── Render ─────────── */
  if (!planId || !platformItem) {
    return null;
  }

  const hasSessionTrialPreview = Boolean(
    resolveTrialMetadata({ trial: sessionTrial })
  );

  // Show skeleton on the very first load (no estimate yet and no session trial to preview)
  if (isEstimating && !estimatedOrder && !hasSessionTrialPreview) {
    return <NeoOrderSummarySkeleton />;
  }

  const billingPlatformName =
    platformItem.billingPlatformName ||
    itemDetails?.billingPlatformName ||
    checkoutPageConfDto?.billingPlatformName;

  const planName = resolveCatalogItemDisplayName(platformItem, {
    billingPlatformName,
    fallbackId: planId,
  });
  const itemDescription = (() => {
    if (platformItem.description && platformItem.description !== planName) {
      return platformItem.description;
    }
    if (
      platformItem.planName &&
      platformItem.planName !== planName &&
      !looksTechnicalId(platformItem.planName)
    ) {
      return platformItem.planName;
    }
    return "";
  })();

  const displayTotalDue =
    trialMeta && isZeroAmountTrial(trialMeta, totalDue)
      ? Number(trialMeta.amountDueNow ?? 0)
      : totalDue;
  const trialCopy = (() => {
    if (trialMeta && isZeroAmountTrial(trialMeta, displayTotalDue)) {
      const endLabel = formatTrialEndDate(trialMeta.trialEnd, language);
      if (endLabel) {
        return t("trial.freeUntil", {
          variables: {
            date: endLabel,
            price: fmt(recurringTotal),
            period: periodNounText,
          },
        });
      }
      return t("trial.noChargeToday", {
        variables: { price: fmt(recurringTotal), period: periodNounText },
      });
    }
    if (hasAppliedCoupon && discount > 0) {
      return t("checkout.neo.afterCouponExpires", {
        variables: { price: fmt(recurringTotal), period: periodNounText },
      });
    }
    return null;
  })();

  return (
    <div>
      <NeoSubscribeLabel>
        {t("checkout.neo.subscribeTo", { variables: { merchant: merchantName } })}
      </NeoSubscribeLabel>
      <NeoBigPrice>{fmt(displayTotalDue)}</NeoBigPrice>
      {trialCopy && <NeoTrialCopy>{trialCopy}</NeoTrialCopy>}

      {/* Plan + nested add-ons */}
      <NeoPlanGroup>
        <NeoLineItemRow>
          <NeoLineThumb $color={checkoutPageConfDto?.colour}>
            {checkoutPageConfDto?.logoUrl && (
              <img src={checkoutPageConfDto.logoUrl} alt={merchantName} />
            )}
          </NeoLineThumb>
          <NeoLineBody>
            <NeoLineTitle>{planName || merchantName}</NeoLineTitle>
            {itemDescription && <NeoLineSub>{itemDescription}</NeoLineSub>}
            <NeoLineSub>{billedText}</NeoLineSub>
          </NeoLineBody>
          <NeoLinePrice>{fmt(planUnitAmount)}</NeoLinePrice>
        </NeoLineItemRow>

        {(selectedAddons || []).length > 0 && (
          <NeoAddonList>
            {(selectedAddons || []).map((addon, idx) => {
              const itemPriceId = addon.itemPriceId || addon.id;
              const estimateLine = estimateLinesById.get(itemPriceId);
              const catalog = resolveCatalogOrHydratedPrice(
                platformItemDetails,
                itemPriceId,
                addon
              );
              const fromEstimate = estimateLine
                ? resolveEstimateLineDisplay(estimateLine, displayOptions)
                : null;
              const catalogUnitMajor = catalog
                ? minorToMajor(catalog.price || 0, currencyCode)
                : minorToMajor(getAddonUnitPriceMinor(addon), currencyCode);
              const addonQty = Number(
                fromEstimate?.qty ?? addon.quantity ?? 1
              );
              const unit =
                fromEstimate?.displayUnit > 0
                  ? fromEstimate.displayUnit
                  : catalogUnitMajor;
              const total =
                fromEstimate?.displayTotal > 0
                  ? fromEstimate.displayTotal
                  : unit * addonQty;
              const addonTitle = resolveCatalogItemDisplayName(
                {
                  ...catalog,
                  ...addon,
                  description: estimateLine?.description || catalog?.description,
                },
                {
                  billingPlatformName,
                  fallbackId: itemPriceId,
                  index: idx,
                }
              );
              const addonSubtext =
                catalog?.planName &&
                catalog.planName !== addonTitle &&
                !looksTechnicalId(catalog.planName)
                  ? catalog.planName
                  : null;
              const showQuantityBreakdown =
                supportsLineItemQuantity(billingPlatformName, catalog || addon) &&
                addonQty > 1 &&
                unit > 0;

              return (
                <NeoAddonRow key={itemPriceId || idx}>
                  <NeoAddonBody>
                    <NeoAddonLabel>
                      {t("checkout.neo.addonLabel")}
                    </NeoAddonLabel>
                    <NeoLineTitle>{addonTitle}</NeoLineTitle>
                    {addonSubtext && <NeoLineSub>{addonSubtext}</NeoLineSub>}
                    {showQuantityBreakdown && (
                      <NeoLineSub>
                        {addonQty} × {fmt(unit)}
                      </NeoLineSub>
                    )}
                  </NeoAddonBody>
                  <NeoLinePrice>{fmt(total)}</NeoLinePrice>
                </NeoAddonRow>
              );
            })}
          </NeoAddonList>
        )}
      </NeoPlanGroup>

      {showSubtotal && (
        <NeoSummaryRow>
          <span>{t("checkout.orderSummary.subtotal")}</span>
          <span>{fmt(subtotalDisplay)}</span>
        </NeoSummaryRow>
      )}

      {discount > 0 && (
        <>
          <NeoSummaryRow>
            {hasAppliedCoupon ? (
              <NeoCouponPill>
                <TagIcon />
                <span>{effectiveCouponCodes[0]}</span>
              </NeoCouponPill>
            ) : (
              <span>{t("checkout.orderSummary.discount")}</span>
            )}
            <span>−{fmt(discount)}</span>
          </NeoSummaryRow>
          {hasAppliedCoupon && (
            <NeoCouponNote>
              {t("checkout.neo.couponOff", {
                variables: { amount: fmt(discount), period: periodNounText },
              })}
            </NeoCouponNote>
          )}
        </>
      )}

      {/* Coupon redeem/remove UI when COUPON_CODE is enabled */}
      {isCouponEnabled && (
        <div style={{ margin: "8px 0 12px" }}>
          <NeoCouponInputRow>
            <NeoCouponInput
              type="text"
              value={couponInput}
              placeholder={t("checkout.orderSummary.couponPlaceholder")}
              onChange={(e) => setCouponInput(e.target.value)}
              disabled={isApplyingCoupon}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleRedeemCoupon();
                }
              }}
            />
            <NeoCouponRedeemBtn
              type="button"
              onClick={handleRedeemCoupon}
              disabled={isApplyingCoupon || !couponInput.trim()}
            >
              {isApplyingCoupon
                ? t("checkout.orderSummary.couponApplying")
                : t("checkout.orderSummary.couponRedeem")}
            </NeoCouponRedeemBtn>
          </NeoCouponInputRow>
          {couponError && <NeoCouponError>{couponError}</NeoCouponError>}
          {appliedCoupons.length > 0 && (
            <NeoCouponChipRow>
              {appliedCoupons.map((c) => (
                <NeoCouponChip key={c}>
                  <span>{c}</span>
                  <NeoCouponRemoveBtn
                    type="button"
                    onClick={() => handleRemoveCoupon(c)}
                    disabled={isApplyingCoupon}
                    aria-label={t("checkout.orderSummary.couponRemove")}
                  >
                    ×
                  </NeoCouponRemoveBtn>
                </NeoCouponChip>
              ))}
              {appliedCoupons.length > 1 && (
                <NeoCouponClearAll
                  type="button"
                  onClick={handleClearCoupons}
                  disabled={isApplyingCoupon}
                >
                  {t("checkout.orderSummary.couponClearAll")}
                </NeoCouponClearAll>
              )}
            </NeoCouponChipRow>
          )}
        </div>
      )}

      {isTaxExclusive && totalTaxAmount > 0 && (
        <NeoSummaryRow>
          <NeoTaxLabel>
            {t("checkout.orderSummary.tax")} <InfoIcon />
          </NeoTaxLabel>
          <span>{isEstimating ? "…" : fmt(totalTaxAmount)}</span>
        </NeoSummaryRow>
      )}

      <NeoDivider />

      <NeoSummaryRow $bold>
        <span>{t("checkout.neo.totalDueToday")}</span>
        <span>{fmt(totalDue)}</span>
      </NeoSummaryRow>
      {!isTaxExclusive && totalTaxAmount > 0 && (
        <NeoCouponNote>
          {t("checkout.orderSummary.includesTax", {
            variables: { amount: fmt(totalTaxAmount) },
          })}
        </NeoCouponNote>
      )}
    </div>
  );
};

export default NeoOrderSummary;
